import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Plus, Pencil, Trash2, ToggleLeft, ToggleRight, X, Image as ImageIcon, GripVertical, Monitor, Smartphone } from 'lucide-react';
import ImageGallery from '../../components/ImageGallery';
import '../../styles/admin/Banners.css';

const API_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5000');

const toIntToggle = (value, fallback = 1) => {
  if (value === 1 || value === '1' || value === true) return 1;
  if (value === 0 || value === '0' || value === false || value === null) return 0;
  return fallback;
};

const isToggleOn = (value, fallback = 1) => toIntToggle(value, fallback) === 1;

const emptyForm = {
  id: null,
  slide_order: 0,
  banner_type: 4,
  media_type: 'image',
  image_desktop: '',
  image_mobile: '',
  image_tab: '',
  video_desktop: '',
  video_mobile: '',
  video_tab: '',
  image_alignment_desktop: 'center',
  image_alignment_tab: 'center',
  image_alignment_mobile: 'center',
  image_offset_desktop: 0,
  image_offset_tab: 0,
  image_offset_mobile: 0,
  content_alignment: 'center',
  badge_enabled: 1,
  badge_text: '',
  badge_avatars: 4,
  title_enabled: 1,
  title_part1: '',
  title_part2: '',
  subtitle_enabled: 1,
  subtitle: '',
  benefits_items: '',
  emphasis_text: '',
  cta_enabled: 1,
  cta_primary: '',
  cta_secondary: '',
  stats_enabled: 1,
  stat1_enabled: 1,
  stat1_icon: 'users',
  stat1_value: '',
  stat1_label: '',
  stat2_enabled: 1,
  stat2_icon: 'star',
  stat2_value: '',
  stat2_label: '',
  overlay_enabled: 1,
  overlay_color: '#000000',
  overlay_opacity: 0.5,
  is_active: 1,
  // Style 1 specific fields
  style1_content_box_width: '35%',
  style1_content_box_height: 'auto',
  style1_content_box_padding: '3.5rem',
  style1_content_box_margin: '0.5rem',
  style1_content_box_bg_color1: '#6b73a8',
  style1_content_box_bg_color2: '#5a6299',
  style1_content_box_opacity: 0.75,
  style1_title_font_size: '40',
  style1_title_font_weight: 'normal',
  style1_title_color: '#ffffff',
  style1_title_line_height: '1.3',
  style1_title_max_width: 'none',
  style1_description_font_size: '18',
  style1_description_color: '#ffffff',
  style1_description_opacity: 0.95,
  style1_description_max_width: '24rem',
  style1_button_bg_color: '#ffffff',
  style1_button_bg_opacity: 0.2,
  style1_button_text_color: '#ffffff',
  style1_button_border_color: '#ffffff',
  style1_button_border_opacity: 0.3,
  style1_button_max_width: '20rem',
  style1_button_padding: '1rem 2rem',
  style1_text_alignment: 'right',
  style1_content_position: 'right'
};

const getTypeStyleDefaults = (type) => {
  if (type === 2 || type === 4) {
    return {
      overlay_color: '#605043',
      overlay_opacity: 0.62,
      style1_content_box_bg_color1: '#6b5b4f',
      style1_content_box_bg_color2: '#5e4f45',
      style1_content_box_opacity: 0.8,
      style1_button_bg_color: '#d8d4dd',
      style1_button_bg_opacity: 0.8,
      style1_button_text_color: '#ffffff',
      style1_button_border_color: '#ffffff',
      style1_button_border_opacity: 0.0
    };
  }

  if (type === 3) {
    return {
      overlay_color: '#4f4671',
      overlay_opacity: 0.5,
      style1_content_box_bg_color1: '#4f4671',
      style1_content_box_bg_color2: '#4f4671',
      style1_content_box_opacity: 0.5,
      style1_button_bg_color: '#d8d4dd',
      style1_button_bg_opacity: 0.8,
      style1_button_text_color: '#ffffff',
      style1_button_border_color: '#ffffff',
      style1_button_border_opacity: 0.0
    };
  }

  return {
    overlay_color: '#000000',
    overlay_opacity: 0.5
  };
};

const normalizeHexColor = (value, fallback) => {
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(trimmed) ? trimmed : fallback;
};

const getBannerComponentSwatches = (banner) => {
  const titleColor = normalizeHexColor(
    banner.style1_title_color || banner.style1Config?.title?.color,
    '#6d28d9'
  );
  const subtitleColor = normalizeHexColor(
    banner.style1_description_color || banner.style1Config?.description?.color,
    '#be185d'
  );
  const ctaColor = normalizeHexColor(
    banner.style1_button_bg_color || banner.style1Config?.button?.backgroundColor,
    '#166534'
  );
  const overlayColor = normalizeHexColor(
    banner.overlay_color || banner.overlay?.color,
    '#4b5563'
  );

  return {
    title: titleColor,
    subtitle: subtitleColor,
    badge: '#1d4ed8',
    cta: ctaColor,
    overlay: overlayColor,
    stats: '#92400e'
  };
};

export default function Banners() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState({ desktop: false, tab: false, mobile: false });
  const [uploadingVideo, setUploadingVideo] = useState({ desktop: false, tab: false, mobile: false });
  const [uploadError, setUploadError] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [activeImageField, setActiveImageField] = useState(null);
  const fileInputRef = useRef({ desktop: null, tab: null, mobile: null });
  const videoInputRef = useRef({ desktop: null, tab: null, mobile: null });
  const [dragActive, setDragActive] = useState({ desktop: false, tab: false, mobile: false });

  const fetchBanners = async () => {
    try {
      const res = await fetch(`${API_URL}/api/banners/admin/all`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setBanners(data);
      }
    } catch (error) {
      console.error('Error fetching banners:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm, slide_order: banners.length + 1 });
    setDialogOpen(true);
  };

  const applyTypePreset = (type, prev) => {
    const styleDefaults = getTypeStyleDefaults(type);
    if (type === 2) {
      return {
        ...prev,
        ...styleDefaults,
        banner_type: 2,
        badge_enabled: 0,
        stats_enabled: 0,
        stat1_enabled: 0,
        stat2_enabled: 0,
        cta_enabled: 1,
        overlay_enabled: 1,
        title_enabled: 1,
        subtitle_enabled: 1,
        benefits_items: prev.benefits_items || 'Workout from home\nNo gym\nMinimal equipment\nWorkout anytime',
        cta_secondary: '',
        content_alignment: 'center'
      };
    }
    if (type === 3) {
      return {
        ...prev,
        ...styleDefaults,
        banner_type: 3,
        badge_enabled: 0,
        badge_text: '',
        badge_avatars: 0,
        stats_enabled: 0,
        stat1_enabled: 0,
        stat1_value: '',
        stat1_label: '',
        stat2_enabled: 0,
        stat2_value: '',
        stat2_label: '',
        cta_enabled: 1,
        overlay_enabled: 1,
        title_enabled: 1,
        subtitle_enabled: 1,
        benefits_items: prev.benefits_items || 'Workout from home\nNo gym\nMinimal equipment\nWorkout anytime',
        content_alignment: 'left'
      };
    }
    return { ...prev, ...styleDefaults, banner_type: type };
  };

  const openEdit = (banner) => {
    console.log('Opening banner for edit:', banner);
    const styleDefaults = getTypeStyleDefaults(banner.banner_type || 4);
    const resolvedOverlay = banner.overlay || {};
    const resolvedStyle = banner.style1Config || {};
    const resolvedContentBox = resolvedStyle.contentBox || {};
    const resolvedTitle = resolvedStyle.title || {};
    const resolvedDescription = resolvedStyle.description || {};
    const resolvedButton = resolvedStyle.button || {};
    const resolvedLayout = resolvedStyle.layout || {};
    setEditingId(banner.id);
    setForm({
      id: banner.id,
      slide_order: banner.slide_order,
      banner_type: banner.banner_type || 4,
      media_type: banner.media_type === 'video' ? 'video' : 'image',
      image_desktop: banner.image_desktop || '',
      image_mobile: banner.image_mobile || '',
      image_tab: banner.image_tab || '',
      video_desktop: banner.video_desktop || '',
      video_mobile: banner.video_mobile || '',
      video_tab: banner.video_tab || '',
      image_alignment_desktop: banner.image_alignment_desktop || 'center',
      image_alignment_tab: banner.image_alignment_tab || 'center',
      image_alignment_mobile: banner.image_alignment_mobile || 'center',
      image_offset_desktop: banner.image_offset_desktop || 0,
      image_offset_tab: banner.image_offset_tab || 0,
      image_offset_mobile: banner.image_offset_mobile || 0,
      badge_enabled: toIntToggle(banner.badge_enabled, 1),
      badge_text: banner.badge_text || '',
      badge_avatars: banner.badge_avatars || 4,
      content_alignment: banner.content_alignment || 'center',
      title_enabled: toIntToggle(banner.title_enabled, 1),
      title_part1: banner.title_part1 || '',
      title_part2: banner.title_part2 || '',
      subtitle_enabled: toIntToggle(banner.subtitle_enabled, 1),
      subtitle: banner.subtitle || '',
      benefits_items: Array.isArray(banner.benefits_items)
        ? banner.benefits_items.join('\n')
        : (banner.benefits_items || ''),
      emphasis_text: banner.emphasis_text || '',
      cta_enabled: toIntToggle(banner.cta_enabled, 1),
      cta_primary: banner.cta_primary || '',
      cta_secondary: banner.cta_secondary || '',
      stats_enabled: toIntToggle(banner.stats_enabled, 1),
      stat1_enabled: toIntToggle(banner.stat1_enabled, 1),
      stat1_icon: banner.stat1_icon || 'users',
      stat1_value: banner.stat1_value || '',
      stat1_label: banner.stat1_label || '',
      stat2_enabled: toIntToggle(banner.stat2_enabled, 1),
      stat2_icon: banner.stat2_icon || 'star',
      stat2_value: banner.stat2_value || '',
      stat2_label: banner.stat2_label || '',
      overlay_enabled: toIntToggle(banner.overlay_enabled, 1),
      overlay_color: banner.overlay_color || resolvedOverlay.color || styleDefaults.overlay_color,
      overlay_opacity: banner.overlay_opacity !== undefined ? banner.overlay_opacity : (resolvedOverlay.opacity ?? styleDefaults.overlay_opacity),
      is_active: toIntToggle(banner.is_active, 1),
      // Style 1 specific fields
      style1_content_box_width: banner.style1_content_box_width || resolvedContentBox.width || '35%',
      style1_content_box_height: banner.style1_content_box_height || resolvedContentBox.height || 'auto',
      style1_content_box_padding: banner.style1_content_box_padding || resolvedContentBox.padding || '3.5rem',
      style1_content_box_margin: banner.style1_content_box_margin || resolvedContentBox.margin || '0.5rem',
      style1_content_box_bg_color1: banner.style1_content_box_bg_color1 || resolvedContentBox.backgroundColor1 || styleDefaults.style1_content_box_bg_color1 || '#6b73a8',
      style1_content_box_bg_color2: banner.style1_content_box_bg_color2 || resolvedContentBox.backgroundColor2 || styleDefaults.style1_content_box_bg_color2 || '#5a6299',
      style1_content_box_opacity: banner.style1_content_box_opacity !== undefined ? banner.style1_content_box_opacity : (resolvedContentBox.opacity ?? styleDefaults.style1_content_box_opacity ?? 0.75),
      style1_title_font_size: banner.style1_title_font_size || resolvedTitle.fontSize || '40',
      style1_title_font_weight: banner.style1_title_font_weight || resolvedTitle.fontWeight || 'normal',
      style1_title_color: banner.style1_title_color || resolvedTitle.color || '#ffffff',
      style1_title_line_height: banner.style1_title_line_height || resolvedTitle.lineHeight || '1.3',
      style1_title_max_width: banner.style1_title_max_width || resolvedTitle.maxWidth || 'none',
      style1_description_font_size: banner.style1_description_font_size || resolvedDescription.fontSize || '18',
      style1_description_color: banner.style1_description_color || resolvedDescription.color || '#ffffff',
      style1_description_opacity: banner.style1_description_opacity !== undefined ? banner.style1_description_opacity : (resolvedDescription.opacity ?? 0.95),
      style1_description_max_width: banner.style1_description_max_width || resolvedDescription.maxWidth || '24rem',
      style1_button_bg_color: banner.style1_button_bg_color || resolvedButton.backgroundColor || styleDefaults.style1_button_bg_color || '#ffffff',
      style1_button_bg_opacity: banner.style1_button_bg_opacity !== undefined ? banner.style1_button_bg_opacity : (resolvedButton.backgroundOpacity ?? styleDefaults.style1_button_bg_opacity ?? 0.2),
      style1_button_text_color: banner.style1_button_text_color || resolvedButton.textColor || styleDefaults.style1_button_text_color || '#ffffff',
      style1_button_border_color: banner.style1_button_border_color || resolvedButton.borderColor || styleDefaults.style1_button_border_color || '#ffffff',
      style1_button_border_opacity: banner.style1_button_border_opacity !== undefined ? banner.style1_button_border_opacity : (resolvedButton.borderOpacity ?? styleDefaults.style1_button_border_opacity ?? 0.3),
      style1_button_max_width: banner.style1_button_max_width || resolvedButton.maxWidth || '20rem',
      style1_button_padding: banner.style1_button_padding || resolvedButton.padding || '1rem 2rem',
      style1_text_alignment: banner.style1_text_alignment || resolvedLayout.textAlignment || 'right',
      style1_content_position: banner.style1_content_position || resolvedLayout.contentPosition || 'right'
    });
    setDialogOpen(true);
  };

  const isType2 = form.banner_type === 2;
  const isType3 = form.banner_type === 3;
  const supportsStats = !isType2 && !isType3;

  const handleSave = async () => {
    setSaving(true);

    const normalizeMediaPath = (value) => {
      if (!value) return '';
      if (!value.startsWith('http')) return value;
      try {
        const parsed = new URL(value);
        if (parsed.pathname.startsWith('/uploads/')) {
          return parsed.pathname;
        }
      } catch (error) {
        console.warn('Failed to parse media URL:', value, error);
      }
      return value.startsWith(API_URL) ? value.substring(API_URL.length) : value;
    };

    const imageDesktop = normalizeMediaPath(form.image_desktop || '');
    const imageMobile = normalizeMediaPath(form.image_mobile || '');
    const imageTab = normalizeMediaPath(form.image_tab || '');
    const videoDesktop = normalizeMediaPath(form.video_desktop || '');
    const videoMobile = normalizeMediaPath(form.video_mobile || '');
    const videoTab = normalizeMediaPath(form.video_tab || '');
    const mediaType = form.media_type === 'video' ? 'video' : 'image';

    if (mediaType === 'video' && !videoDesktop) {
      setUploadError('Desktop video is required for video banners');
      setSaving(false);
      return;
    }
    if (mediaType === 'image' && !imageDesktop) {
      setUploadError('Desktop image is required for image banners');
      setSaving(false);
      return;
    }

    // Ensure toggle values are integers 0 or 1
    const payload = {
      ...form,
      media_type: mediaType,
      image_desktop: imageDesktop,
      image_mobile: imageMobile,
      image_tab: imageTab,
      video_desktop: videoDesktop,
      video_mobile: videoMobile,
      video_tab: videoTab,
      image_alignment_desktop: form.image_alignment_desktop || 'center',
      image_alignment_tab: form.image_alignment_tab || 'center',
      image_alignment_mobile: form.image_alignment_mobile || 'center',
      image_offset_desktop: parseFloat(form.image_offset_desktop) || 0,
      image_offset_tab: parseFloat(form.image_offset_tab) || 0,
      image_offset_mobile: parseFloat(form.image_offset_mobile) || 0,
      content_alignment: form.content_alignment || 'center',
      banner_type: parseInt(form.banner_type, 10) || 4,
      badge_enabled: toIntToggle(form.badge_enabled, 1),
      title_enabled: toIntToggle(form.title_enabled, 1),
      subtitle_enabled: toIntToggle(form.subtitle_enabled, 1),
      cta_enabled: toIntToggle(form.cta_enabled, 1),
      stats_enabled: toIntToggle(form.stats_enabled, 1),
      stat1_enabled: toIntToggle(form.stat1_enabled, 1),
      stat2_enabled: toIntToggle(form.stat2_enabled, 1),
      overlay_enabled: toIntToggle(form.overlay_enabled, 1),
      benefits_items: String(form.benefits_items || '')
        .split('\n')
        .map((item) => item.trim())
        .filter(Boolean),
    };

    // Keep data aligned with actual frontend layouts for type 2/3.
    if (payload.banner_type === 2 || payload.banner_type === 3) {
      payload.badge_enabled = 0;
      payload.badge_text = '';
      payload.badge_avatars = 0;
      payload.stats_enabled = 0;
      payload.stat1_enabled = 0;
      payload.stat1_value = '';
      payload.stat1_label = '';
      payload.stat2_enabled = 0;
      payload.stat2_value = '';
      payload.stat2_label = '';
    }

    console.log('Saving banner with payload:', payload);

    try {
      const url = editingId 
        ? `${API_URL}/api/banners/${editingId}`
        : `${API_URL}/api/banners`;
      
      const method = editingId ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        fetchBanners();
        setDialogOpen(false);
      } else {
        const errorData = await res.json();
        alert(errorData.error || 'Failed to save banner');
      }
    } catch (error) {
      console.error('Error saving banner:', error);
      alert('Failed to save banner');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this banner? This cannot be undone.')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/banners/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      if (res.ok) {
        fetchBanners();
      }
    } catch (error) {
      console.error('Error deleting banner:', error);
    }
  };

  const toggleStatus = async (banner) => {
    const newStatus = banner.is_active === 1 ? 0 : 1;
    try {
      const res = await fetch(`${API_URL}/api/banners/${banner.id}/status`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({ is_active: newStatus })
      });
      
      if (res.ok) {
        fetchBanners();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  // Handle image upload
  const handleImageUpload = async (file, field) => {
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Only image files (JPG, PNG, GIF, WebP) are allowed');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File size must be less than 5MB');
      return;
    }
    
    setUploading({ ...uploading, [field]: true });
    setUploadError(null);
    
    const formData = new FormData();
    formData.append('image', file);
    
    try {
      const res = await fetch(`${API_URL}/api/upload/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: formData
      });
      
      const data = await res.json();
      
      if (data.success) {
        const fieldMap = {
          desktop: 'image_desktop',
          tab: 'image_tab',
          mobile: 'image_mobile'
        };
        setForm(f => ({ ...f, [fieldMap[field] || 'image_desktop']: data.url }));
      } else {
        setUploadError(data.error || 'Failed to upload image');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setUploadError('Failed to upload image');
    } finally {
      setUploading({ ...uploading, [field]: false });
    }
  };

  const handleVideoUpload = async (file, field) => {
    if (!file) return;

    const allowedTypes = ['video/mp4', 'video/webm'];
    const extOk = /\.(mp4|webm)$/i.test(file.name || '');
    if (!allowedTypes.includes(file.type) && !extOk) {
      setUploadError('Only MP4 or WebM video files are allowed');
      return;
    }

    const maxMb = 50;
    if (file.size > maxMb * 1024 * 1024) {
      setUploadError(`Video must be less than ${maxMb}MB`);
      return;
    }

    setUploadingVideo((prev) => ({ ...prev, [field]: true }));
    setUploadError(null);

    const formData = new FormData();
    formData.append('video', file);

    try {
      const res = await fetch(`${API_URL}/api/upload/banner-video`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: formData
      });

      const data = await res.json();
      if (data.success) {
        const fieldMap = {
          desktop: 'video_desktop',
          tab: 'video_tab',
          mobile: 'video_mobile'
        };
        setForm((f) => ({ ...f, [fieldMap[field] || 'video_desktop']: data.url }));
      } else {
        setUploadError(data.error || 'Failed to upload video');
      }
    } catch (error) {
      console.error('Error uploading banner video:', error);
      setUploadError('Failed to upload video');
    } finally {
      setUploadingVideo((prev) => ({ ...prev, [field]: false }));
    }
  };
  
  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      handleImageUpload(file, field);
    }
  };

  const handleVideoFileChange = (e, field) => {
    const file = e.target.files?.[0];
    if (file) {
      handleVideoUpload(file, field);
    }
    e.target.value = '';
  };
  
  const triggerFileInput = (field) => {
    fileInputRef.current[field]?.click();
  };

  const triggerVideoInput = (field) => {
    videoInputRef.current[field]?.click();
  };
  
  const clearImage = (field) => {
    const fieldMap = {
      'desktop': 'image_desktop',
      'mobile': 'image_mobile',
      'tab': 'image_tab'
    };
    setForm(f => ({ ...f, [fieldMap[field]]: '' }));
  };

  const clearVideo = (field) => {
    const fieldMap = {
      desktop: 'video_desktop',
      mobile: 'video_mobile',
      tab: 'video_tab'
    };
    setForm((f) => ({ ...f, [fieldMap[field]]: '' }));
  };

  const openGallery = (field) => {
    setActiveImageField(field);
    setGalleryOpen(true);
  };

  const handleSelectImage = (url) => {
    const fieldMap = {
      'desktop': 'image_desktop',
      'mobile': 'image_mobile',
      'tab': 'image_tab'
    };
    setForm(f => ({ 
      ...f, 
      [fieldMap[activeImageField]]: url 
    }));
  };

  // Drag and drop handlers
  const handleDrag = useCallback((e, field, isActive) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive({ ...dragActive, [field]: isActive });
  }, [dragActive]);

  const handleDrop = useCallback(async (e, field) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive({ ...dragActive, [field]: false });

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      await handleImageUpload(e.dataTransfer.files[0], field);
    }
  }, [dragActive]);

  // Helper to get full image URL
  const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${API_URL}${path}`;
  };

  // Reorder banners
  const moveBanner = async (index, direction) => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === banners.length - 1)) {
      return;
    }

    const newBanners = [...banners];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    // Swap orders
    const tempOrder = newBanners[index].slide_order;
    newBanners[index].slide_order = newBanners[targetIndex].slide_order;
    newBanners[targetIndex].slide_order = tempOrder;
    
    // Sort by order
    newBanners.sort((a, b) => a.slide_order - b.slide_order);
    
    setBanners(newBanners);

    // Save to backend
    try {
      const orders = newBanners.map((b, i) => ({ id: b.id, slide_order: i + 1 }));
      await fetch(`${API_URL}/api/banners/reorder`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        },
        body: JSON.stringify({ orders })
      });
      fetchBanners();
    } catch (error) {
      console.error('Error reordering banners:', error);
    }
  };

  return (
    <div className="admin-banners">
      <div className="banners-header">
        <div>
          <h1>Banner Manager</h1>
          <p>Manage homepage hero slides. Drag to reorder, click to edit.</p>
        </div>
        <button className="btn-primary" onClick={openCreate}>
          <Plus className="h-4 w-4" /> New Banner
        </button>
      </div>

      <div className="banners-table-card">
        <table className="banners-table">
          <thead>
            <tr>
              <th style={{ width: '50px' }}></th>
              <th>Banner</th>
              <th>Order</th>
              <th>Status</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="text-center py-10">Loading…</td>
              </tr>
            ) : banners.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-10">No banners yet. Create one!</td>
              </tr>
            ) : (
              banners.map((banner, index) => (
                <tr key={banner.id}>
                  <td>
                    <div className="drag-handle">
                      <GripVertical className="h-4 w-4" />
                    </div>
                  </td>
                  <td>
                    <div className="banner-info">
                      <div className="banner-images">
                        {banner.media_type === 'video' && banner.video_desktop ? (
                          <div className="banner-thumb" title="Video banner">
                            {banner.image_desktop ? (
                              <img
                                src={getImageUrl(banner.image_desktop)}
                                alt="Poster"
                                className="banner-thumb"
                              />
                            ) : (
                              <video
                                src={getImageUrl(banner.video_desktop)}
                                muted
                                playsInline
                                className="banner-thumb"
                              />
                            )}
                          </div>
                        ) : banner.image_desktop ? (
                          <img 
                            src={getImageUrl(banner.image_desktop)} 
                            alt="Desktop" 
                            className="banner-thumb"
                          />
                        ) : (
                          <div className="banner-thumb-placeholder">
                            <Monitor className="h-4 w-4" />
                          </div>
                        )}
                        {banner.image_tab ? (
                          <img 
                            src={getImageUrl(banner.image_tab)} 
                            alt="Tab" 
                            className="banner-thumb tab"
                          />
                        ) : banner.image_desktop ? (
                          <div className="banner-thumb tab-fallback">
                            <img 
                              src={getImageUrl(banner.image_desktop)} 
                              alt="Tab (from desktop)" 
                              className="banner-thumb tab"
                            />
                            <span className="fallback-indicator">T</span>
                          </div>
                        ) : (
                          <div className="banner-thumb-placeholder tab">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="4" y="4" width="16" height="12" rx="2"></rect>
                            </svg>
                          </div>
                        )}
                        {banner.image_mobile ? (
                          <img 
                            src={getImageUrl(banner.image_mobile)} 
                            alt="Mobile" 
                            className="banner-thumb mobile"
                          />
                        ) : (
                          <div className="banner-thumb-placeholder mobile">
                            <Smartphone className="h-4 w-4" />
                          </div>
                        )}
                      </div>
                      <div className="banner-details">
                        {(() => {
                          const isType2 = Number(banner.banner_type || 4) === 2;
                          const isType3 = Number(banner.banner_type || 4) === 3;
                          const supportsBadge = !isType2 && !isType3;
                          const supportsStats = !isType2 && !isType3;
                          const swatches = getBannerComponentSwatches(banner);
                          return (
                            <>
                        <p className="banner-title">
                          {isToggleOn(banner.title_enabled, 1)
                            ? ([banner.title_part1 || '', banner.title_part2 || ''].join('').trim() || 'Untitled banner')
                            : 'Title hidden'}
                        </p>
                        <div className="banner-meta">
                          <span className="type-pill">Type {banner.banner_type || 4}</span>
                        </div>
                        <div className="banner-components">
                          {isToggleOn(banner.title_enabled, 1) && (
                            <span className="component-tag title-tag">
                              <span className="tag-swatch" style={{ backgroundColor: swatches.title }} />
                              Title
                            </span>
                          )}
                          {isToggleOn(banner.subtitle_enabled, 1) && (
                            <span className="component-tag subtitle-tag">
                              <span className="tag-swatch" style={{ backgroundColor: swatches.subtitle }} />
                              Subtitle
                            </span>
                          )}
                          {supportsBadge && isToggleOn(banner.badge_enabled, 1) && (
                            <span className="component-tag badge-tag">
                              <span className="tag-swatch" style={{ backgroundColor: swatches.badge }} />
                              Badge
                            </span>
                          )}
                          {isToggleOn(banner.cta_enabled, 1) && (
                            <span className="component-tag cta-tag">
                              <span className="tag-swatch" style={{ backgroundColor: swatches.cta }} />
                              CTA
                            </span>
                          )}
                          {isToggleOn(banner.overlay_enabled, 1) && (
                            <span className="component-tag overlay-tag">
                              <span className="tag-swatch" style={{ backgroundColor: swatches.overlay }} />
                              Overlay
                            </span>
                          )}
                          {supportsStats && isToggleOn(banner.stats_enabled, 1) && (
                            <span className="component-tag stats-tag">
                              <span className="tag-swatch" style={{ backgroundColor: swatches.stats }} />
                              Stats
                              {(!isToggleOn(banner.stat1_enabled, 1) || !isToggleOn(banner.stat2_enabled, 1)) && (
                                <span className="stats-partial">
                                  ({isToggleOn(banner.stat1_enabled, 1) ? '1' : '0'}+{isToggleOn(banner.stat2_enabled, 1) ? '1' : '0'})
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="order-controls">
                      <button 
                        className="order-btn"
                        onClick={() => moveBanner(index, 'up')}
                        disabled={index === 0}
                      >
                        ↑
                      </button>
                      <span className="order-number">{banner.slide_order}</span>
                      <button 
                        className="order-btn"
                        onClick={() => moveBanner(index, 'down')}
                        disabled={index === banners.length - 1}
                      >
                        ↓
                      </button>
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${banner.is_active === 1 ? 'badge-active' : 'badge-inactive'}`}>
                      {banner.is_active === 1 ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="text-right">
                    <div className="action-buttons">
                      <button 
                        className="btn-icon" 
                        onClick={() => toggleStatus(banner)} 
                        title="Toggle status"
                      >
                        {banner.is_active === 1 ? 
                          <ToggleRight className="h-4 w-4 text-green-500" /> : 
                          <ToggleLeft className="h-4 w-4 text-gray-400" />
                        }
                      </button>
                      <button 
                        className="btn-icon" 
                        onClick={() => openEdit(banner)}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button 
                        className="btn-icon text-red-500" 
                        onClick={() => handleDelete(banner.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Dialog */}
      {dialogOpen && (
        <div className="dialog-overlay" onClick={() => setDialogOpen(false)}>
          <div className="dialog-content banner-dialog" onClick={e => e.stopPropagation()}>
            <div className="dialog-header">
              <h2>{editingId ? 'Edit Banner' : 'Create New Banner'}</h2>
            </div>
            <div className="dialog-body">
              {/* Order */}
              <div className="form-row three-col">
                <div className="form-group">
                  <label>Slide Order</label>
                  <input 
                    type="number" 
                    value={form.slide_order}
                    onChange={e => setForm(f => ({ ...f, slide_order: parseInt(e.target.value) || 0 }))}
                    placeholder="e.g., 1, 2, 3"
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <div 
                    onClick={() => setForm(f => ({ ...f, is_active: f.is_active === 1 ? 0 : 1 }))}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      cursor: 'pointer',
                      padding: '8px 0'
                    }}
                  >
                    {form.is_active === 1 ? (
                      <>
                        <ToggleRight className="h-8 w-8 text-green-500" />
                        <span style={{ color: '#166534', fontWeight: 500 }}>Active</span>
                      </>
                    ) : (
                      <>
                        <ToggleLeft className="h-8 w-8 text-gray-400" />
                        <span style={{ color: '#6b7280', fontWeight: 500 }}>Inactive</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label>Banner Type</label>
                  <select
                    value={form.banner_type}
                    disabled={!!editingId}
                    onChange={e => {
                      const typeVal = parseInt(e.target.value, 10);
                      setForm(f => applyTypePreset(typeVal, f));
                    }}
                  >
                    <option value={1}>Type 1</option>
                    <option value={2}>Type 2</option>
                    <option value={3}>Type 3</option>
                    <option value={4}>Type 4</option>
                    <option value={5}>Type 5</option>
                  </select>
                  <small className="field-hint">{editingId ? '' : 'Choose layout while creating'}</small>
                </div>
                <div className="form-group">
                  <label>Media Type</label>
                  <select
                    value={form.media_type === 'video' ? 'video' : 'image'}
                    onChange={(e) => setForm((f) => ({ ...f, media_type: e.target.value }))}
                  >
                    <option value="image">Image</option>
                    <option value="video">Video</option>
                  </select>
                  <small className="field-hint">
                    {form.media_type === 'video'
                      ? 'Muted autoplay on homepage; carousel waits until the clip ends'
                      : 'Standard image banner'}
                  </small>
                </div>
              </div>

              {form.media_type === 'video' && (
                <div className="form-section">
                  <label className="section-label">Banner Videos</label>
                  <small className="field-hint" style={{ display: 'block', marginBottom: '0.75rem' }}>
                    MP4 or WebM. Desktop video is required; tab/mobile fall back to desktop if empty.
                  </small>
                  <div className="form-row three-images">
                    {['desktop', 'tab', 'mobile'].map((field) => {
                      const valueKey = field === 'desktop' ? 'video_desktop' : field === 'tab' ? 'video_tab' : 'video_mobile';
                      const videoUrl = form[valueKey];
                      const label = field === 'desktop' ? 'Desktop Video' : field === 'tab' ? 'Tab Video' : 'Mobile Video';
                      return (
                        <div className="form-group" key={field}>
                          <label>{label}{field === 'desktop' ? ' *' : ''}</label>
                          <input
                            type="file"
                            ref={(el) => { videoInputRef.current[field] = el; }}
                            onChange={(e) => handleVideoFileChange(e, field)}
                            accept="video/mp4,video/webm"
                            style={{ display: 'none' }}
                          />
                          {videoUrl ? (
                            <div className="image-preview-banner">
                              <video
                                src={getImageUrl(videoUrl)}
                                muted
                                playsInline
                                controls
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                              <button
                                type="button"
                                className="btn-remove-image"
                                onClick={() => clearVideo(field)}
                              >
                                <X size={14} />
                              </button>
                              <div
                                className="image-change-hint"
                                onClick={() => triggerVideoInput(field)}
                                style={{ cursor: 'pointer' }}
                              >
                                <span>Click to replace</span>
                              </div>
                            </div>
                          ) : (
                            <div
                              className={`upload-banner ${uploadingVideo[field] ? 'uploading' : ''}`}
                              onClick={() => triggerVideoInput(field)}
                            >
                              {uploadingVideo[field] ? (
                                <div className="upload-spinner-small" />
                              ) : (
                                <>
                                  {field === 'mobile' ? <Smartphone size={24} /> : <Monitor size={24} />}
                                  <span>{field === 'desktop' ? 'Desktop' : field === 'tab' ? 'Tab' : 'Mobile'}</span>
                                  <small>Upload video</small>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Images / posters */}
              <div className="form-section">
                <label className="section-label">
                  {form.media_type === 'video' ? 'Poster Images (optional)' : 'Banner Images'}
                </label>
                {form.media_type === 'video' && (
                  <small className="field-hint" style={{ display: 'block', marginBottom: '0.75rem' }}>
                    Shown before playback or if autoplay is blocked.
                  </small>
                )}
                <div className="form-row three-images">
                  {/* Desktop Image */}
                  <div className="form-group">
                    <label>Desktop Image</label>
                    <input
                      type="file"
                      ref={el => fileInputRef.current.desktop = el}
                      onChange={(e) => handleFileChange(e, 'desktop')}
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      style={{ display: 'none' }}
                    />
                    
                  {form.image_desktop ? (
                    <div 
                      className="image-preview-banner" 
                      onClick={() => openGallery('desktop')}
                    >
                      <img 
                        src={getImageUrl(form.image_desktop)}
                        alt="Desktop preview"
                        crossOrigin="anonymous"
                      />
                      <div className="form-group-inline">
                        <label>Alignment</label>
                        <select
                          value={form.image_alignment_desktop}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setForm(f => ({ ...f, image_alignment_desktop: e.target.value }))}
                        >
                          <option value="top">Top</option>
                          <option value="center">Center</option>
                          <option value="bottom">Bottom</option>
                        </select>
                      </div>
                      <button 
                        type="button" 
                        className="btn-remove-image"
                        onClick={(e) => { e.stopPropagation(); clearImage('desktop'); }}
                        >
                          <X size={14} />
                        </button>
                        <div className="image-change-hint">
                          <Monitor size={16} />
                          <span>Click to change</span>
                        </div>
                      </div>
                    ) : (
                      <div 
                        className={`upload-banner ${uploading.desktop ? 'uploading' : ''} ${dragActive.desktop ? 'drag-active' : ''}`}
                        onClick={() => openGallery('desktop')}
                        onDragEnter={(e) => handleDrag(e, 'desktop', true)}
                        onDragLeave={(e) => handleDrag(e, 'desktop', false)}
                        onDragOver={(e) => handleDrag(e, 'desktop', true)}
                        onDrop={(e) => handleDrop(e, 'desktop')}
                      >
                        {uploading.desktop ? (
                          <div className="upload-spinner-small" />
                        ) : (
                          <>
                            <Monitor size={24} />
                            <span>Desktop</span>
                            <small>Click or drop</small>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Tab Image */}
                  <div className="form-group">
                    <label>Tab Image</label>
                    <input
                      type="file"
                      ref={el => fileInputRef.current.tab = el}
                      onChange={(e) => handleFileChange(e, 'tab')}
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      style={{ display: 'none' }}
                    />
                    
                    {form.image_tab ? (
                      <div 
                        className="image-preview-banner tab" 
                        onClick={() => openGallery('tab')}
                      >
                        <img 
                          src={getImageUrl(form.image_tab)}
                          alt="Tab preview"
                          crossOrigin="anonymous"
                        />
                        <div className="form-group-inline">
                          <label>Alignment</label>
                          <select
                            value={form.image_alignment_tab}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setForm(f => ({ ...f, image_alignment_tab: e.target.value }))}
                          >
                            <option value="top">Top</option>
                            <option value="center">Center</option>
                            <option value="bottom">Bottom</option>
                          </select>
                        </div>
                        <button 
                          type="button" 
                          className="btn-remove-image"
                          onClick={(e) => { e.stopPropagation(); clearImage('tab'); }}
                        >
                          <X size={14} />
                        </button>
                        <div className="image-change-hint">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="4" y="4" width="16" height="12" rx="2"></rect>
                          </svg>
                          <span>Click to change</span>
                        </div>
                      </div>
                    ) : form.image_desktop ? (
                      /* Show desktop image preview when no tab image set */
                      <div 
                        className="image-preview-banner tab preview-fallback" 
                        onClick={() => openGallery('tab')}
                        title="Using desktop image. Click to set custom tab image."
                      >
                        <img 
                          src={getImageUrl(form.image_desktop)}
                          alt="Tab preview (from desktop)"
                          crossOrigin="anonymous"
                          className="fallback-preview"
                        />
                        <div className="fallback-badge">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="4" y="4" width="16" height="12" rx="2"></rect>
                          </svg>
                          <span>Tab Preview</span>
                        </div>
                        <div className="image-change-hint">
                          <span>Click to customize</span>
                        </div>
                      </div>
                    ) : (
                      <div 
                        className={`upload-banner tab ${uploading.tab ? 'uploading' : ''} ${dragActive.tab ? 'drag-active' : ''}`}
                        onClick={() => openGallery('tab')}
                        onDragEnter={(e) => handleDrag(e, 'tab', true)}
                        onDragLeave={(e) => handleDrag(e, 'tab', false)}
                        onDragOver={(e) => handleDrag(e, 'tab', true)}
                        onDrop={(e) => handleDrop(e, 'tab')}
                      >
                        {uploading.tab ? (
                          <div className="upload-spinner-small" />
                        ) : (
                          <>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="4" y="4" width="16" height="12" rx="2"></rect>
                            </svg>
                            <span>Tab</span>
                            <small>Click or drop</small>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Mobile Image */}
                  <div className="form-group">
                    <label>Mobile Image</label>
                    <input
                      type="file"
                      ref={el => fileInputRef.current.mobile = el}
                      onChange={(e) => handleFileChange(e, 'mobile')}
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      style={{ display: 'none' }}
                    />
                    
                    {form.image_mobile ? (
                      <div 
                        className="image-preview-banner mobile" 
                        onClick={() => openGallery('mobile')}
                      >
                        <img 
                          src={getImageUrl(form.image_mobile)}
                          alt="Mobile preview"
                          crossOrigin="anonymous"
                        />
                        <div className="form-group-inline">
                          <label>Alignment</label>
                          <select
                            value={form.image_alignment_mobile}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => setForm(f => ({ ...f, image_alignment_mobile: e.target.value }))}
                          >
                            <option value="top">Top</option>
                            <option value="center">Center</option>
                            <option value="bottom">Bottom</option>
                          </select>
                        </div>
                        <button 
                          type="button" 
                          className="btn-remove-image"
                          onClick={(e) => { e.stopPropagation(); clearImage('mobile'); }}
                        >
                          <X size={14} />
                        </button>
                        <div className="image-change-hint">
                          <Smartphone size={16} />
                          <span>Click to change</span>
                        </div>
                      </div>
                    ) : (
                      <div 
                        className={`upload-banner mobile ${uploading.mobile ? 'uploading' : ''} ${dragActive.mobile ? 'drag-active' : ''}`}
                        onClick={() => openGallery('mobile')}
                        onDragEnter={(e) => handleDrag(e, 'mobile', true)}
                        onDragLeave={(e) => handleDrag(e, 'mobile', false)}
                        onDragOver={(e) => handleDrag(e, 'mobile', true)}
                        onDrop={(e) => handleDrop(e, 'mobile')}
                      >
                        {uploading.mobile ? (
                          <div className="upload-spinner-small" />
                        ) : (
                          <>
                            <Smartphone size={24} />
                            <span>Mobile</span>
                            <small>Click or drop</small>
                          </>
                        )}
                      </div>
                    )}
                </div>
              </div>
              {uploadError && <p className="upload-error">{uploadError}</p>}
            </div>

            {/* Title */}
            <div className="form-section">
              <div className="section-header">
                <label className="section-label">Title</label>
                <div 
                  className="component-toggle"
                  onClick={() => setForm(f => ({ ...f, title_enabled: f.title_enabled === 1 ? 0 : 1 }))}
                >
                  {form.title_enabled === 1 ? (
                    <ToggleRight className="h-6 w-6 text-green-500" />
                  ) : (
                    <ToggleLeft className="h-6 w-6 text-gray-400" />
                  )}
                  <span className={form.title_enabled === 1 ? 'text-green-600' : 'text-gray-500'}>
                    {form.title_enabled === 1 ? 'On' : 'Off'}
                  </span>
                </div>
              </div>
              {form.title_enabled === 1 && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Title Text (use \n for line breaks)</label>
                    <textarea 
                      value={form.title_part1}
                      onChange={e => setForm(f => ({ ...f, title_part1: e.target.value }))}
                      placeholder="e.g., Fitness that works\nwith your busy life"
                      rows={3}
                    />
                    <small className="field-hint">Use \n to create line breaks in your title</small>
                  </div>
                </div>
              )}
            </div>

            {/* Subtitle */}
            <div className="form-section">
              <div className="section-header">
                <label className="section-label">Description</label>
                <div 
                  className="component-toggle"
                  onClick={() => setForm(f => ({ ...f, subtitle_enabled: f.subtitle_enabled === 1 ? 0 : 1 }))}
                >
                  {form.subtitle_enabled === 1 ? (
                    <ToggleRight className="h-6 w-6 text-green-500" />
                  ) : (
                    <ToggleLeft className="h-6 w-6 text-gray-400" />
                  )}
                  <span className={form.subtitle_enabled === 1 ? 'text-green-600' : 'text-gray-500'}>
                    {form.subtitle_enabled === 1 ? 'On' : 'Off'}
                  </span>
                </div>
              </div>
              {form.subtitle_enabled === 1 && (
                <div className="form-group">
                  <label>Description Text</label>
                  <textarea 
                    value={form.subtitle}
                    onChange={e => setForm(f => ({ ...f, subtitle: e.target.value }))}
                    rows={3}
                    placeholder="Get strong, energized and confident with simple home workouts designed for busy women."
                  />
                </div>
              )}
            </div>

            {(isType2 || isType3 || form.banner_type === 4) && (
              <div className="form-section">
                <div className="section-header">
                  <label className="section-label">Benefits (One Per Line)</label>
                </div>
                <div className="form-group">
                  <textarea
                    value={form.benefits_items}
                    onChange={(e) => setForm((f) => ({ ...f, benefits_items: e.target.value }))}
                    rows={4}
                    placeholder={'Workout from home\nNo gym\nMinimal equipment\nWorkout anytime'}
                  />
                  <small className="field-hint">Shown as checklist items for banner types that support benefits.</small>
                </div>
              </div>
            )}

            {/* CTA Button */}
            <div className="form-section">
              <div className="section-header">
                <label className="section-label">Call to Action Button</label>
                <div 
                  className="component-toggle"
                  onClick={() => setForm(f => ({ ...f, cta_enabled: f.cta_enabled === 1 ? 0 : 1 }))}
                >
                  {form.cta_enabled === 1 ? (
                    <ToggleRight className="h-6 w-6 text-green-500" />
                  ) : (
                    <ToggleLeft className="h-6 w-6 text-gray-400" />
                  )}
                  <span className={form.cta_enabled === 1 ? 'text-green-600' : 'text-gray-500'}>
                    {form.cta_enabled === 1 ? 'On' : 'Off'}
                  </span>
                </div>
              </div>
              {form.cta_enabled === 1 && (
                <div className="form-group">
                  <label>Button Text</label>
                  <input 
                    type="text" 
                    value={form.cta_primary}
                    onChange={e => setForm(f => ({ ...f, cta_primary: e.target.value }))}
                    placeholder="e.g., Begin your workout today"
                  />
                </div>
              )}
            </div>

              {/* Style Controls (shared) */}
              {true && (
                <div className="style1-grid">
                  <div className="style1-header">
                    <h3>Style Controls</h3>
                    <p>Customize layout, colors, and positioning</p>
                  </div>

                  {/* Content Box */}
                  <div className="style1-group">
                    <h4>Content Box</h4>
                    <div className="compact-row">
                      <div className="compact-field">
                        <label>Width</label>
                        <input 
                          type="text" 
                          value={form.style1_content_box_width}
                          onChange={e => setForm(f => ({ ...f, style1_content_box_width: e.target.value }))}
                          placeholder="35%"
                        />
                      </div>
                      <div className="compact-field">
                        <label>Height</label>
                        <input 
                          type="text" 
                          value={form.style1_content_box_height}
                          onChange={e => setForm(f => ({ ...f, style1_content_box_height: e.target.value }))}
                          placeholder="auto"
                        />
                      </div>
                      <div className="compact-field">
                        <label>Padding</label>
                        <input 
                          type="text" 
                          value={form.style1_content_box_padding}
                          onChange={e => setForm(f => ({ ...f, style1_content_box_padding: e.target.value }))}
                          placeholder="3.5rem"
                        />
                      </div>
                    </div>
                    <div className="compact-row">
                      <div className="compact-field">
                        <label>Margin</label>
                        <input 
                          type="text" 
                          value={form.style1_content_box_margin}
                          onChange={e => setForm(f => ({ ...f, style1_content_box_margin: e.target.value }))}
                          placeholder="0.5rem"
                        />
                      </div>
                      <div className="compact-field color-input">
                        <label>BG Color 1</label>
                        <div className="color-picker">
                          <input 
                            type="color" 
                            value={form.style1_content_box_bg_color1}
                            onChange={e => setForm(f => ({ ...f, style1_content_box_bg_color1: e.target.value }))}
                          />
                          <input 
                            type="text" 
                            value={form.style1_content_box_bg_color1}
                            onChange={e => setForm(f => ({ ...f, style1_content_box_bg_color1: e.target.value }))}
                            placeholder="#6b73a8"
                          />
                        </div>
                      </div>
                      <div className="compact-field color-input">
                        <label>BG Color 2</label>
                        <div className="color-picker">
                          <input 
                            type="color" 
                            value={form.style1_content_box_bg_color2}
                            onChange={e => setForm(f => ({ ...f, style1_content_box_bg_color2: e.target.value }))}
                          />
                          <input 
                            type="text" 
                            value={form.style1_content_box_bg_color2}
                            onChange={e => setForm(f => ({ ...f, style1_content_box_bg_color2: e.target.value }))}
                            placeholder="#5a6299"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="compact-row">
                      <div className="compact-field range-field">
                        <label>BG Opacity: {Math.round(form.style1_content_box_opacity * 100)}%</label>
                        <input 
                          type="range" 
                          min="0"
                          max="1"
                          step="0.05"
                          value={form.style1_content_box_opacity}
                          onChange={e => setForm(f => ({ ...f, style1_content_box_opacity: parseFloat(e.target.value) }))}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Title */}
                  <div className="style1-group">
                    <h4>Title</h4>
                    <div className="compact-row">
                      <div className="compact-field">
                        <label>Font Size (px)</label>
                        <input 
                          type="number" 
                          value={form.style1_title_font_size}
                          onChange={e => setForm(f => ({ ...f, style1_title_font_size: e.target.value }))}
                          min="12"
                          max="100"
                          placeholder="40"
                        />
                      </div>
                      <div className="compact-field">
                        <label>Weight</label>
                        <select
                          value={form.style1_title_font_weight}
                          onChange={e => setForm(f => ({ ...f, style1_title_font_weight: e.target.value }))}
                        >
                          <option value="light">Light</option>
                          <option value="normal">Normal</option>
                          <option value="medium">Medium</option>
                          <option value="semibold">Semibold</option>
                          <option value="bold">Bold</option>
                        </select>
                      </div>
                      <div className="compact-field">
                        <label>Line Height</label>
                        <input 
                          type="text" 
                          value={form.style1_title_line_height}
                          onChange={e => setForm(f => ({ ...f, style1_title_line_height: e.target.value }))}
                          placeholder="1.3"
                        />
                      </div>
                    </div>
                    <div className="compact-row">
                      <div className="compact-field color-input">
                        <label>Color</label>
                        <div className="color-picker">
                          <input 
                            type="color" 
                            value={form.style1_title_color}
                            onChange={e => setForm(f => ({ ...f, style1_title_color: e.target.value }))}
                          />
                          <input 
                            type="text" 
                            value={form.style1_title_color}
                            onChange={e => setForm(f => ({ ...f, style1_title_color: e.target.value }))}
                            placeholder="#ffffff"
                          />
                        </div>
                      </div>
                      <div className="compact-field">
                        <label>Max Width</label>
                        <input 
                          type="text" 
                          value={form.style1_title_max_width}
                          onChange={e => setForm(f => ({ ...f, style1_title_max_width: e.target.value }))}
                          placeholder="none"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="style1-group">
                    <h4>Description</h4>
                    <div className="compact-row">
                      <div className="compact-field">
                        <label>Font Size (px)</label>
                        <input 
                          type="number" 
                          value={form.style1_description_font_size}
                          onChange={e => setForm(f => ({ ...f, style1_description_font_size: e.target.value }))}
                          min="10"
                          max="32"
                          placeholder="18"
                        />
                      </div>
                      <div className="compact-field">
                        <label>Max Width</label>
                        <input 
                          type="text" 
                          value={form.style1_description_max_width}
                          onChange={e => setForm(f => ({ ...f, style1_description_max_width: e.target.value }))}
                          placeholder="24rem"
                        />
                      </div>
                      <div className="compact-field range-field">
                        <label>Opacity: {Math.round(form.style1_description_opacity * 100)}%</label>
                        <input 
                          type="range" 
                          min="0"
                          max="1"
                          step="0.05"
                          value={form.style1_description_opacity}
                          onChange={e => setForm(f => ({ ...f, style1_description_opacity: parseFloat(e.target.value) }))}
                        />
                      </div>
                    </div>
                    <div className="compact-row">
                      <div className="compact-field color-input">
                        <label>Color</label>
                        <div className="color-picker">
                          <input 
                            type="color" 
                            value={form.style1_description_color}
                            onChange={e => setForm(f => ({ ...f, style1_description_color: e.target.value }))}
                          />
                          <input 
                            type="text" 
                            value={form.style1_description_color}
                            onChange={e => setForm(f => ({ ...f, style1_description_color: e.target.value }))}
                            placeholder="#ffffff"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Button */}
                  <div className="style1-group">
                    <h4>Button</h4>
                    <div className="compact-row">
                      <div className="compact-field color-input">
                        <label>BG Color</label>
                        <div className="color-picker">
                          <input 
                            type="color" 
                            value={form.style1_button_bg_color}
                            onChange={e => setForm(f => ({ ...f, style1_button_bg_color: e.target.value }))}
                          />
                          <input 
                            type="text" 
                            value={form.style1_button_bg_color}
                            onChange={e => setForm(f => ({ ...f, style1_button_bg_color: e.target.value }))}
                            placeholder="#ffffff"
                          />
                        </div>
                      </div>
                      <div className="compact-field range-field">
                        <label>BG Opacity: {Math.round(form.style1_button_bg_opacity * 100)}%</label>
                        <input 
                          type="range" 
                          min="0"
                          max="1"
                          step="0.05"
                          value={form.style1_button_bg_opacity}
                          onChange={e => setForm(f => ({ ...f, style1_button_bg_opacity: parseFloat(e.target.value) }))}
                        />
                      </div>
                      <div className="compact-field color-input">
                        <label>Text Color</label>
                        <div className="color-picker">
                          <input 
                            type="color" 
                            value={form.style1_button_text_color}
                            onChange={e => setForm(f => ({ ...f, style1_button_text_color: e.target.value }))}
                          />
                          <input 
                            type="text" 
                            value={form.style1_button_text_color}
                            onChange={e => setForm(f => ({ ...f, style1_button_text_color: e.target.value }))}
                            placeholder="#ffffff"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="compact-row">
                      <div className="compact-field color-input">
                        <label>Border Color</label>
                        <div className="color-picker">
                          <input 
                            type="color" 
                            value={form.style1_button_border_color}
                            onChange={e => setForm(f => ({ ...f, style1_button_border_color: e.target.value }))}
                          />
                          <input 
                            type="text" 
                            value={form.style1_button_border_color}
                            onChange={e => setForm(f => ({ ...f, style1_button_border_color: e.target.value }))}
                            placeholder="#ffffff"
                          />
                        </div>
                      </div>
                      <div className="compact-field range-field">
                        <label>Border Opacity: {Math.round(form.style1_button_border_opacity * 100)}%</label>
                        <input 
                          type="range" 
                          min="0"
                          max="1"
                          step="0.05"
                          value={form.style1_button_border_opacity}
                          onChange={e => setForm(f => ({ ...f, style1_button_border_opacity: parseFloat(e.target.value) }))}
                        />
                      </div>
                      <div className="compact-field">
                        <label>Max Width</label>
                        <input 
                          type="text" 
                          value={form.style1_button_max_width}
                          onChange={e => setForm(f => ({ ...f, style1_button_max_width: e.target.value }))}
                          placeholder="20rem"
                        />
                      </div>
                    </div>
                    <div className="compact-row">
                      <div className="compact-field">
                        <label>Padding</label>
                        <input 
                          type="text" 
                          value={form.style1_button_padding}
                          onChange={e => setForm(f => ({ ...f, style1_button_padding: e.target.value }))}
                          placeholder="1rem 2rem"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Layout */}
                  <div className="style1-group">
                    <h4>Layout</h4>
                    <div className="compact-row">
                      <div className="compact-field">
                        <label>Text Alignment</label>
                        <select
                          value={form.style1_text_alignment}
                          onChange={e => setForm(f => ({ ...f, style1_text_alignment: e.target.value }))}
                        >
                          <option value="left">Left</option>
                          <option value="center">Center</option>
                          <option value="right">Right</option>
                        </select>
                      </div>
                      <div className="compact-field">
                        <label>Content Position</label>
                        <select
                          value={form.style1_content_position}
                          onChange={e => setForm(f => ({ ...f, style1_content_position: e.target.value }))}
                        >
                          <option value="left">Left</option>
                          <option value="center">Center</option>
                          <option value="right">Right</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Stats */}
              {supportsStats && (
                <div className="form-section">
                  <div className="section-header">
                    <label className="section-label">Stats</label>
                    <div 
                      className="component-toggle"
                      onClick={() => setForm(f => ({ ...f, stats_enabled: f.stats_enabled === 1 ? 0 : 1 }))}
                    >
                      {form.stats_enabled === 1 ? (
                        <ToggleRight className="h-6 w-6 text-green-500" />
                      ) : (
                        <ToggleLeft className="h-6 w-6 text-gray-400" />
                      )}
                      <span className={form.stats_enabled === 1 ? 'text-green-600' : 'text-gray-500'}>
                        {form.stats_enabled === 1 ? 'On' : 'Off'}
                      </span>
                    </div>
                  </div>
                  {form.stats_enabled === 1 && (
                    <>
                      {/* Stat 1 */}
                      <div className="stat-item-row">
                        <div className="stat-header">
                          <label>Stat 1</label>
                          <div 
                            className="stat-toggle"
                            onClick={() => setForm(f => ({ ...f, stat1_enabled: f.stat1_enabled === 1 ? 0 : 1 }))}
                          >
                            {form.stat1_enabled === 1 ? (
                              <ToggleRight className="h-5 w-5 text-green-500" />
                            ) : (
                              <ToggleLeft className="h-5 w-5 text-gray-400" />
                            )}
                          </div>
                        </div>
                        {form.stat1_enabled === 1 && (
                          <div className="form-row three-col">
                            <select 
                              value={form.stat1_icon}
                              onChange={e => setForm(f => ({ ...f, stat1_icon: e.target.value }))}
                            >
                              <option value="users">Users Icon</option>
                              <option value="star">Star Icon</option>
                            </select>
                            <input 
                              type="text" 
                              value={form.stat1_value}
                              onChange={e => setForm(f => ({ ...f, stat1_value: e.target.value }))}
                              placeholder="Value"
                            />
                            <input 
                              type="text" 
                              value={form.stat1_label}
                              onChange={e => setForm(f => ({ ...f, stat1_label: e.target.value }))}
                              placeholder="Label"
                            />
                          </div>
                        )}
                      </div>

                      {/* Stat 2 */}
                      <div className="stat-item-row">
                        <div className="stat-header">
                          <label>Stat 2</label>
                          <div 
                            className="stat-toggle"
                            onClick={() => setForm(f => ({ ...f, stat2_enabled: f.stat2_enabled === 1 ? 0 : 1 }))}
                          >
                            {form.stat2_enabled === 1 ? (
                              <ToggleRight className="h-5 w-5 text-green-500" />
                            ) : (
                              <ToggleLeft className="h-5 w-5 text-gray-400" />
                            )}
                          </div>
                        </div>
                        {form.stat2_enabled === 1 && (
                          <div className="form-row three-col">
                            <select 
                              value={form.stat2_icon}
                              onChange={e => setForm(f => ({ ...f, stat2_icon: e.target.value }))}
                            >
                              <option value="users">Users Icon</option>
                              <option value="star">Star Icon</option>
                            </select>
                            <input 
                              type="text" 
                              value={form.stat2_value}
                              onChange={e => setForm(f => ({ ...f, stat2_value: e.target.value }))}
                              placeholder="Value"
                            />
                            <input 
                              type="text" 
                              value={form.stat2_label}
                              onChange={e => setForm(f => ({ ...f, stat2_label: e.target.value }))}
                              placeholder="Label"
                            />
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="dialog-bottom">
              <ImageGallery 
                isOpen={galleryOpen} 
                onClose={() => setGalleryOpen(false)}
                onSelect={handleSelectImage}
                currentImage={activeImageField === 'desktop' ? form.image_desktop : activeImageField === 'tab' ? form.image_tab : form.image_mobile}
              />

              <div className="dialog-footer">
                <button className="btn-secondary" onClick={() => setDialogOpen(false)}>
                  Cancel
                </button>
                <button 
                  className="btn-primary" 
                  onClick={handleSave} 
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Banner'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
